(() => {
  if (customElements.get('lr-cart-bundle')) return;

  const selector = 'cart-drawer lr-cart-bundle';
  let adding = false;
  let revision = 0;
  let refreshTimer;
  let refreshController;
  const cartURL = () => window.routes.cart_url;
  const currentBundle = () => document.querySelector(selector);
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  function sectionContextURL() {
    // Keep the active page, locale and draft preview context. A request to a
    // different page must not replace the editor's current section settings.
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    const theme = window.Shopify?.theme;
    if (theme?.id && ['unpublished', 'development'].includes(theme.role)) {
      url.searchParams.set('preview_theme_id', theme.id);
    }
    return url;
  }

  // Replace only our component for app events; checkout, coupons and app widgets
  // remain owned by the existing drawer. Preserve a customer's size selection.
  function applyBundle(html) {
    const current = currentBundle();
    const next = html && parse(html).querySelector('lr-cart-bundle');
    if (!current || !next) return;
    const focused = current.contains(document.activeElement) ? document.activeElement : null;
    current.querySelectorAll('select').forEach((select) => {
      const replacement = next.querySelector(`[id="${select.id}"]`);
      const option = replacement && Array.from(replacement.options).find((item) => item.value === select.value && !item.disabled);
      if (option) replacement.value = select.value;
    });
    // Avoid disturbing focus and live regions when nothing changed on the server.
    if (current.outerHTML === next.outerHTML) return;
    const focusId = focused && focused.id;
    const focusedProduct = focused && focused.closest('[data-product-id]')?.dataset.productId;
    current.replaceWith(next);
    if (focusId) next.querySelector(`[id="${focusId}"]`)?.focus();
    else if (focusedProduct) next.querySelector(`[data-product-id="${focusedProduct}"] button`)?.focus();
  }

  async function fetchSections(signal) {
    const url = sectionContextURL();
    url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
    const response = await fetch(url, { signal, cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to refresh your cart. Please try again.');
    const sections = await response.json();
    if (!sections['cart-drawer']) throw new Error('Unable to refresh your cart. Please try again.');
    return sections;
  }

  function invalidateRefresh() {
    revision += 1;
    clearTimeout(refreshTimer);
    refreshController?.abort();
  }

  function scheduleRefresh() {
    if (!currentBundle()) return;
    invalidateRefresh();
    const expectedRevision = revision;
    refreshTimer = setTimeout(async () => {
      refreshController = new AbortController();
      try {
        const sections = await fetchSections(refreshController.signal);
        if (expectedRevision === revision) applyBundle(sections['cart-drawer']);
      } catch (error) {
        // A background read failing does not invalidate Shopify's rendered
        // product membership or cards. Only withdraw a previously confirmed
        // savings claim until fresh discount data arrives. Never hide the UI.
        if (error.name !== 'AbortError' && expectedRevision === revision && currentBundle()) {
          const bundle = currentBundle();
          const note = bundle.querySelector('[data-bundle-note]');
          if (bundle.dataset.components === '2' && note) {
            note.textContent = 'Final discounts are confirmed at checkout.';
          }
          console.warn('[Links & Rinks bundle] Background refresh failed; keeping the rendered tracker.', error);
        }
      }
    }, 80);
  }

  function onCartUpdate(event) {
    const data = event?.cartData || event?.detail?.cartData || event?.detail;
    const html = data?.sections?.['cart-drawer'];
    if (html) {
      invalidateRefresh();
      applyBundle(html);
    } else {
      scheduleRefresh();
    }
  }

  function renderCart(sections) {
    const drawer = document.querySelector('cart-drawer');
    const html = sections?.['cart-drawer'];
    const documentFragment = html && parse(html);
    if (!drawer || !documentFragment?.querySelector('.drawer__inner') || !documentFragment.querySelector('.cart-drawer__footer')) return false;
    invalidateRefresh();
    drawer.classList.toggle('is-empty', !!documentFragment.querySelector('cart-drawer.is-empty'));
    drawer.renderContents({ sections });
    const icon = document.getElementById('cart-icon-bubble');
    const iconHTML = sections['cart-icon-bubble'] && parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
    if (icon && iconHTML) icon.innerHTML = iconHTML.innerHTML;
    return true;
  }

  class CartBundle extends HTMLElement {
    connectedCallback() {
      this.onSubmit = this.onSubmit || this.addProduct.bind(this);
      this.onChange = this.onChange || ((event) => this.updateForm(event.target.closest('form')));
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('change', this.onChange);
      this.querySelectorAll('form').forEach((form) => this.updateForm(form));
    }

    disconnectedCallback() {
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('change', this.onChange);
    }

    updateForm(form) {
      if (!form) return;
      const input = form.elements.id;
      const option = input.selectedOptions?.[0];
      form.querySelector('[data-bundle-add]').disabled = adding || !input.value || !!option?.disabled || form.dataset.added === 'true';
      if (option?.dataset.price) form.querySelector('[data-bundle-price]').textContent = option.dataset.price;
    }

    showError(message) {
      const error = (currentBundle() || this).querySelector('[data-bundle-error]');
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    }

    async addProduct(event) {
      event.preventDefault();
      const form = event.target.closest('form[data-product-id]');
      if (!form || adding || !form.reportValidity()) return;
      const input = form.elements.id;
      if (!input.value || input.selectedOptions?.[0]?.disabled || form.dataset.added === 'true') return;
      const variantId = input.value;
      const button = form.querySelector('[data-bundle-add]');
      const error = this.querySelector('[data-bundle-error]');
      if (error) error.hidden = true;
      adding = true;
      this.querySelectorAll('form').forEach((item) => this.updateForm(item));
      button.textContent = 'Adding…';
      this.setAttribute('aria-busy', 'true');
      let added = false;

      try {
        // Recheck product IDs at click time, including another variant of the
        // same product. Shopify validates inventory again on the actual add.
        const cartResponse = await fetch(`${cartURL()}.js`, { cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('Unable to check your cart. Please try again.');
        const cart = await cartResponse.json();
        if (cart.items.some((item) => String(item.product_id) === form.dataset.productId)) {
          scheduleRefresh();
          throw new Error('This product is already in your cart.');
        }
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: sectionContextURL().pathname + sectionContextURL().search,
          }),
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          // A 422 can mean a cart rule, not necessarily sold-out inventory.
          // Use Shopify's message and the next server render for availability.
          throw new Error(typeof result.description === 'string' ? result.description : 'This option could not be added. Please choose another available option.');
        }
        added = true;
        form.dataset.added = 'true';
        let sections = result.sections;
        if (!renderCart(sections)) {
          sections = await fetchSections();
          if (!renderCart(sections)) throw new Error('Unable to display the updated cart.');
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lr-cart-bundle', cartData: { ...result, sections } });
        }
        // The added product may remove the entire recommendation area.
        const focusTarget = document.querySelector('cart-drawer .lr-bundle__heading') || document.querySelector('cart-drawer .drawer__inner');
        focusTarget?.setAttribute('tabindex', '-1');
        focusTarget?.focus();
      } catch (error) {
        this.showError(added ? 'Added to your cart. Use View Cart to see your updated cart.' : error.message);
        if (added) scheduleRefresh();
      } finally {
        adding = false;
        button.textContent = added ? 'Added' : 'Add';
        this.removeAttribute('aria-busy');
        currentBundle()?.querySelectorAll('form').forEach((item) => currentBundle().updateForm(item));
      }
    }
  }

  customElements.define('lr-cart-bundle', CartBundle);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
  }
  // Theme/app conventions; Shopify's AJAX API itself has no universal DOM event.
  ['cartUpdated', 'cart:updated', 'cart:change', 'cart:refresh', 'cart:updated:ajax'].forEach((name) => {
    document.addEventListener(name, onCartUpdate);
  });
  // The editor has already rendered the section using the merchant's current
  // settings. Do not overwrite it with another request for saved settings.
  ['shopify:section:load', 'shopify:section:unload'].forEach((name) => {
    document.addEventListener(name, (event) => {
      if (event.detail?.sectionId === 'cart-drawer' || event.target.querySelector?.('lr-cart-bundle')) {
        invalidateRefresh();
      }
    });
  });
  document.addEventListener('click', (event) => {
    if (event.target.closest('#cart-icon-bubble')) scheduleRefresh();
  });
  window.addEventListener('pageshow', (event) => { if (event.persisted) scheduleRefresh(); });

  // Observe completed app AJAX mutations without replacing fetch or XHR (which
  // may also be used by Omnisend, return protection and accelerated checkout).
  if (typeof PerformanceObserver !== 'undefined') {
    try {
      const observer = new PerformanceObserver((list) => {
        if (list.getEntries().some((entry) => {
          if (!['fetch', 'xmlhttprequest'].includes(entry.initiatorType)) return false;
          const url = new URL(entry.name, window.location.origin);
          return url.origin === window.location.origin && /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname);
        })) scheduleRefresh();
      });
      observer.observe({ type: 'resource' });
    } catch (_) { /* Theme events and bundled sections remain the primary path. */ }
  }
})();
